import React from "react";
import { DatePicker } from "../../../../../react-core/Datepicker/DatePicker";
import { Lightbox } from "../../../../../react-core/Html/Components/Lightbox";
import { RadiosEditor } from "../../../../../react-core/Html/RadiosEditor";
import { SelectEditor } from "../../../../../react-core/Html/SelectEditor";
import { TextBoxEditor } from "../../../../../react-core/Html/TextBoxEditor";
import {
    AlphanumericWithSpecialCharacters,
    Alphanumeric,
    LeiCode,
} from "../../../../../react-core/Html/Validation/RegExPatterns";
import { JsonDateParser } from "../../../../../react-core/Utils/JsonDateParser";
import { CompanyType } from "../../../../../Wrap/Constants/Enums/Customer/CompanyType";
import { CustomerType } from "../../../../../Wrap/Constants/Enums/Customer/CustomerType";
import { FATCABusinessType } from "../../../../../Wrap/Constants/Enums/Customer/FATCABusinessType";
import { CountryCode } from "../../../../../Wrap/Constants/Enums/Domain/CountryCode";
import { HierarchyLevelName } from "../../../../../Wrap/Constants/Enums/HierarchyLevelName";
import { RoleType } from "../../../../../Wrap/Constants/Enums/RoleType";
import { AccountRoleByActorDetails } from "../../../../../Wrap/Models/Domains/Account/AccountRoleByActorDetails";
import { TrustDetail } from "../../../../../Wrap/Models/Domains/Account/TrustDetail";
import { Organisation } from "../../../../../Wrap/Models/Models/Customer/Organisation";
import { HierarchyIdHelper } from "../../../../Shared/Common/HierarchyIdHelper";
import { ACCEPT_DATE_FORMATS, ContentDatePicker } from "../../../../Shared/Entities/Constants/DateConstants";
import { CountryCodeMap } from "../../../../Shared/Entities/Maps/CountryCodeMap";
import { IProcessComponentProps, ProcessComponent } from "../../../../Shared/Process/Process/ProcessComponent";
import { IAddClientModel } from "../AddClient/Components/AddClientWizard";
import { FATCACountry } from "../AddClient/Components/Organisation/FATCACountry";
import {
    CompanyTypeMap,
    CompanyTypeOption,
    CustomerTypeArraysMap,
    FATCABusinessTypeMap,
    FATCABusinessTypeOption,
    LawtoApplyMap,
    LawtoApplyOption,
    TrustTypeMap,
} from "../AddClient/Resource/Contents";
import { ClientRecordAction } from "./Action/ClientRecordAction";
import { SectionTable } from "./Components/SectionTable";
import { ClientRecordTab, customerStatusMap } from "./Resource/Contents";
import { AccountRoleStore, ClientRecordStore } from "./Store/Variable";

interface IOrganisationDetailState {
    organisationDetail: Organisation;
    isOrganisationDetailLoading: boolean;
    trustDetail: TrustDetail;
    accountRoleDetails: Array<AccountRoleByActorDetails>;
    showLightbox: boolean;
    data: Organisation;
}
interface IOrganisationDetailProps extends IProcessComponentProps {
    id: string;
    customerType?: CustomerType;
    hasReadWriteAccess: boolean;
}
export class OrganisationDetails extends ProcessComponent<IOrganisationDetailProps, IOrganisationDetailState> {
    constructor(props: IOrganisationDetailProps) {
        super(props);
        this.state = {
            organisationDetail: null,
            isOrganisationDetailLoading: true,
            trustDetail: null,
            accountRoleDetails: null,
            showLightbox: false,
            data: null,
        };
        this.subscribeToStore(ClientRecordStore, () => this.onStoreChange());
        this.subscribeToStore(AccountRoleStore, () => this.onAccountRoleStoreChange());
    }

    private onAccountRoleStoreChange() {
        const data = AccountRoleStore.getData();
        if (data.length > 0) {
            this.setState({ accountRoleDetails: data });
        }
    }

    componentDidMount() {
        super.componentDidMount();
        ClientRecordAction.getOrganisationDetail(this.props.id);
        ClientRecordAction.getAccountRoleDetail(
            this.props.id.toString(),
            (results: Array<AccountRoleByActorDetails>) => {
                if (results.length > 0 && results?.some((x) => x?.AccountRoleType == RoleType.TrustAccountHolder)) {
                    const trustAccountHolder = results?.find((x) => x?.AccountRoleType == RoleType.TrustAccountHolder);
                    const hierarchyId = HierarchyIdHelper.toHierarchyIdString(
                        HierarchyLevelName.Account,
                        trustAccountHolder?.AccountId
                    );
                    ClientRecordAction.getTrustDetails(hierarchyId, (trustDetail) => {
                        this.setState({ trustDetail: trustDetail });
                    });
                }
            }
        );
    }

    private onStoreChange() {
        const data = ClientRecordStore.getData();
        if (data[ClientRecordTab.OrganisationDetails]) {
            const organisation: Organisation = data[ClientRecordTab.OrganisationDetails];
            if (
                organisation.FatcaCountries &&
                organisation.FatcaCountries.length > 0 &&
                organisation.FatcaCountries[0].CountryOfTaxResidency != CountryCode.GBR
            ) {
                organisation.IsTaxPurposeOtherThanUk = true;
            } else {
                organisation.IsTaxPurposeOtherThanUk = false;
            }

            this.setState({
                isOrganisationDetailLoading: false,
                organisationDetail: organisation,
            });
        }
    }

    private onOrganisationDetailSave(data) {
        if (this.isValid()) {
            if (data?.FATCABusinessType == FATCABusinessType.InvestmentBody) {
                this.setState({ showLightbox: true, data: data });
                return false;
            } else {
                this.SaveDetail(data);
                return true;
            }
        }
        return false;
    }

    private SaveDetail(data: Organisation) {
        this.setState({ isOrganisationDetailLoading: true });
        ClientRecordAction.putOrganisationDetail(data, this.props.id);
    }

    private getOrganisationDetailsColConfig(data: Organisation, role: Array<AccountRoleByActorDetails>) {
        const { trustDetail } = this.state;
        if (!data) {return [];}
        const config = [
            { title: "Organisation type", data: CustomerTypeArraysMap[data.CustomerType] },
            { title: "Organisation status", data: role?.length ? customerStatusMap[data.Status] : "Prospect" },
            { title: "Organisation name", data: data.Name },
            { title: "New or existing", data: data.IsNewTrust ? "New" : "Existing" },
            { title: "LEI code", data: data.LEICode },
            { title: "Law to apply", data: LawtoApplyMap[data.LawToApply] },
            { title: "FATCA/CRS Business Type", data: FATCABusinessTypeMap[data.FATCABusinessType] },
        ];

        data.FatcaCountries?.map((item, index) => {
            config.push({
                title: "Country",
                data: CountryCodeMap.getString(data.FatcaCountries[index]?.CountryOfTaxResidency),
            });
            config.push({
                title: "Tax identification number",
                data: data.FatcaCountries[index]?.TaxIdentificationNumber,
            });
        }),
            config.push({ title: "GIIN", data: data.GIIN });
        config.push({
            title: "Date of incorporation",
            data: JsonDateParser.getFormatDate(data.DateOfIncorporation, "dd/MM/yyyy"),
        });
        config.push({
            title: "Country of incorporation",
            data:
                data.CountryOfIncorporation == CountryCode.NotSpecified
                    ? null
                    : CountryCodeMap.getString(data.CountryOfIncorporation),
        });

        if (data?.CustomerType == CustomerType.CorporateTrust) {
            config.push({ title: "Company type", data: CompanyTypeMap[data.CompanyType] });
        }

        if (trustDetail != null) {
            config.push({ title: "Trust type", data: TrustTypeMap[trustDetail?.TrustType] });
            config.push({ title: "Trust category", data: trustDetail?.TrustCategory });
        }
        return config;
    }

    private renderOrganisationDetailsLightBox(data: Organisation, onDataChange) {
        return (
            <>
                <TextBoxEditor
                    cssWrapper="control-size-430"
                    value={data?.Name}
                    label={data.CustomerType == CustomerType.Trust ? "Trust name" : "Name"}
                    regex={{
                        errorMessage: "Only alphanumeric and special characters allowed.",
                        value: AlphanumericWithSpecialCharacters,
                    }}
                    validationRules={[
                        {
                            errorMessage: "Name cannot be spaces only",
                            validator: (v) => {
                                return v.replace(/\s/g, "").length != 0;
                            },
                        },
                    ]}
                    onChange={(value) => onDataChange({ Name: value })}
                    required={"Please enter name"}
                    maxLength={50}
                />

                {/* <RadiosEditor
                    cssWrapper="rg-inline mb-16"
                    label={"New or existing"}
                    tooltip={"SL TO DEFINE"}
                    selected={data?.CustomerType == CustomerType.CorporateTrust ? false : data?.IsNewTrust}
                    options={[true, false]}
                    getText={(v) => (v ? "New" : "Existing")}
                    disabled={data?.CustomerType == CustomerType.CorporateTrust}
                    onChange={(v) => {}}
                /> */}

                <TextBoxEditor
                    cssWrapper="control-size-430"
                    value={data?.LEICode}
                    tooltip={"LEI means Legal Entity Identifier. It is a code unique to a legal entity or structure and required for transaction reporting purposes. Please see the FCA website for detail."}
                    label={"LEI"}
                    regex={{
                        errorMessage: "LEI Code must contain 20 characters.",
                        value: LeiCode,
                    }}
                    maxLength={20}
                    onChange={(value) => onDataChange({ LEICode: value })}
                />

                <label>Law to apply</label>
                <RadiosEditor
                    cssWrapper="radio-group rg-150 mb-23"
                    selected={data?.LawToApply}
                    options={LawtoApplyOption}
                    getText={(v) => {
                        return LawtoApplyMap[v];
                    }}
                    onChange={(v) =>
                        onDataChange({
                            LawToApply: v,
                        })
                    }
                    disabled
                    required="Please choose Law to apply"
                />

                {data.CustomerType == CustomerType.CorporateTrust && (
                    <SelectEditor
                        options={CompanyTypeOption}
                        getText={(v) => {
                            return CompanyTypeMap[v];
                        }}
                        cssWrapper="control-size-430"
                        required="Please select"
                        validationRules={[
                            {
                                errorMessage: "Please select",
                                validator: (v) => {
                                    return v != CompanyType.NotSpecified;
                                },
                            },
                        ]}
                        label="Company type"
                        selected={data?.CompanyType}
                        onChange={(value) => onDataChange({ CompanyType: value })}
                    />
                )}

                <SelectEditor
                    options={FATCABusinessTypeOption}
                    getText={(v) => {
                        return FATCABusinessTypeMap[v];
                    }}
                    tooltip={`Under UK legislation we are required to collect certain tax information about the type of business that invests with us and possibly report some of that information to HMRC who in turn may share that information with other tax authorities around the world as appropriate.

                    The Foreign Account Tax Compliance Act (‘’FATCA’’) was introduced by the U.S. government in 2010. The UK adopted the frame work into UK legislation with an effective date of 1 July 2014. FATCA requires us as platform provider to undertake specified due diligence, reporting and oversight obligations to detect and deter U.S. tax evasion.
                    
                    The Common Reporting Standard (‘’CRS’’) was introduced by the OECD and implemented into UK law with an effective date of 1 January 2016. Broadly similar to FATCA, the legislation requires us as platform provider to undertake essentially the same due diligence, reporting and oversight obligations to detect and deter tax evasion.
                    
                    Please note that we are unable to provide tax advice in respect of the business type or questions on tax residency in respect of the business. In case of any additional questions we would refer you to your tax advisor and/or the HMRC guidance available online.`}
                    tooltipCss={"is-info-icon ml-5 is-wide"}
                    cssWrapper="control-size-430"
                    required="Please select"
                    validationRules={[
                        {
                            errorMessage: "Please select",
                            validator: (v) => {
                                return v != FATCABusinessType.NotSpecified;
                            },
                        },
                    ]}
                    label="FATCA/CRS Business Type"
                    selected={data?.FATCABusinessType}
                    onChange={(v) => {
                        onDataChange({
                            FatcaCountries: [],
                            IsTaxPurposeOtherThanUk: false,
                            GIIN: null,
                            FATCABusinessType: v,
                        });
                    }}
                />
                {(data?.FATCABusinessType == FATCABusinessType.InvestmentBody ||
                    data?.FATCABusinessType == FATCABusinessType.ActiveNFFE) && (
                    <>
                        <label>Is the entity resident for tax purposes anywhere other than the UK?</label>
                        <RadiosEditor
                            cssWrapper="radio-group rg-150 mb-23"
                            selected={data?.IsTaxPurposeOtherThanUk}
                            options={[true, false]}
                            getText={(v) => {
                                return v ? "Yes" : "No";
                            }}
                            onChange={(v) => {
                                if (v == false) {
                                    onDataChange({
                                        IsTaxPurposeOtherThanUk: v,
                                        FatcaCountries: [],
                                    });
                                } else {
                                    onDataChange({ IsTaxPurposeOtherThanUk: v, FatcaCountries: [{}] });
                                }
                            }}
                            required="Please choose"
                            disabled={true}
                        />
                    </>
                )}
                {data?.IsTaxPurposeOtherThanUk == true && (
                    <FATCACountry
                        data={{ Organisation: data } as IAddClientModel}
                        onChange={(v) => {}}
                        onChangeFromOrganisationDetailTab={(v) => {
                            onDataChange({ FatcaCountries: v });
                        }}
                        onRef={(ref) => {
                            this.appendChild(ref);
                        }}
                        disable={true}
                    />
                )}

                {data?.FATCABusinessType === FATCABusinessType.FFI && (
                    <TextBoxEditor
                        cssWrapper="control-size-430"
                        value={data?.GIIN}
                        regex={{ errorMessage: "Please enter alphanumeric", value: Alphanumeric }}
                        label={"GIIN"}
                        maxLength={19}
                        onChange={(value) => onDataChange({ GIIN: value })}
                        required="Please enter"
                    />
                )}

                <DatePicker
                    label={"Date of incorporation"}
                    formGroupCssWrapper={"control-size-430"}
                    selected={JsonDateParser.toDateNoOffset(data.DateOfIncorporation)}
                    alwaysValidateOnUpdate={false}
                    atMost={{
                        value: new Date(),
                        errorMessage: ContentDatePicker.CannotBeInTheFuture,
                    }}
                    showYearDropdown={true}
                    onChange={(value) => {
                        onDataChange({ DateOfIncorporation: JsonDateParser.toJsonDateNoOffset(value) });
                    }}
                    todayButton={ContentDatePicker.Today}
                    isClearable={true}
                    dateFormat={ContentDatePicker.DateFormat}
                    acceptDateFormats={ACCEPT_DATE_FORMATS}
                />

                <SelectEditor
                    cssWrapper="control-size-430"
                    options={[null].concat(CountryCodeMap.getArray())}
                    getText={(v) => {
                        return v != null ? CountryCodeMap.getString(v) : "";
                    }}
                    onChange={(v) => {
                        onDataChange({ CountryOfIncorporation: v });
                    }}
                    validationRules={[
                        { errorMessage: "Please select", validator: (v) => v !== CountryCode.NotSpecified },
                    ]}
                    selected={data.CountryOfIncorporation}
                    label="Country of incorporation"
                />
            </>
        );
    }

    private lightbox;

    render() {
        const { organisationDetail, isOrganisationDetailLoading, accountRoleDetails } = this.state;
        return (
            <div className="panel-subtle pl-34 pt-38">
                <SectionTable
                    isLoading={isOrganisationDetailLoading}
                    sectionHeader={"Organisation details"}
                    columnConfigs={this.getOrganisationDetailsColConfig(organisationDetail, accountRoleDetails)}
                    lightboxContent={(data, onDataChange) => this.renderOrganisationDetailsLightBox(data, onDataChange)}
                    onSave={(data) => this.onOrganisationDetailSave(data)}
                    data={organisationDetail}
                    sectionHeaderCss={"mb-40"}
                    cssWrapper={"mb-20"}
                    canEdit={this.props?.hasReadWriteAccess == true}
                    onRef={(lightbox) => (this.lightbox = lightbox)}
                />
                {this.state.showLightbox && (
                    <Lightbox
                        title=""
                        show={this.state.showLightbox}
                        cssWrapper="is-default-lightbox"
                        isClickOutsideClose={false}
                        onClose={() => this.setState({ showLightbox: false, data: null })}
                    >
                        <p>
                            Prior to amending the Trust type to Investment Body (Passive NFE/NFFE) please ensure Abrdn
                            have received an updated entity self-certification form detailing the controlling persons
                            details as this needs to be manually added into the platform by Operations as Interested
                            parties. If the platform Trust type is updated prior to receiving this information then it
                            will be a FATCA CRS breach.
                        </p>
                        <button
                            className="btn primary"
                            onClick={() => {
                                this.SaveDetail(this.state.data);
                                this.setState({ showLightbox: false, data: null });
                                if (this.lightbox) {this.lightbox(false);}
                            }}
                        >
                            Save
                        </button>
                        <button
                            className="btn secondary ml-10"
                            onClick={() => this.setState({ showLightbox: false, data: null })}
                        >
                            Cancel
                        </button>
                    </Lightbox>
                )}
            </div>
        );
    }
}
